import { CSSProperties } from "react";

interface Coordinate {
  xFeet: number;
  yFeet: number;
  zFeet: number;
  result?: string;
}

interface PointOfContactSideProps {
  style?: CSSProperties;
  coordinates?: Coordinate[];
}

const getColorForCoordinate = (result?: string): string => {
  // Simple color scheme: black for hits, red for outs
  if (result === "Hits") return "#000000";
  if (result === "Outs") return "#ef4444";
  return "#000000";
};

const ORIGIN_Y_IN_CHART_SCALE = 15;
const ORIGIN_Z_IN_CHART_SCALE = 63.016;

const HEIGHT_MIN_IN_FEET = 0;
const HEIGHT_MAX_IN_FEET = 3.73;

const WIDTH_MIN_IN_FEET = 0;
const WIDTH_MAX_IN_FEET = 17 / 12; //dividing by 12 because the width in 17 inches

const HEIGHT_MIN_IN_CHART_SCALE = 18.24;
const HEIGHT_MAX_IN_CHART_SCALE = ORIGIN_Z_IN_CHART_SCALE;

const WIDTH_MIN_IN_CHART_SCALE = ORIGIN_Y_IN_CHART_SCALE;
const WIDTH_MAX_IN_CHART_SCALE = ORIGIN_Y_IN_CHART_SCALE + 17;

export const PointOfContactSide: React.FC<PointOfContactSideProps> = ({
  style,
  coordinates,
}) => {
  const legendItems = coordinates
    ? Array.from(
        new Set(coordinates.map(({ result }) => JSON.stringify({ result }))),
      ).map((item) => JSON.parse(item))
    : [];

  return (
    <div
      style={{
        ...style,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Chart */}
      <div style={{ position: "relative", width: "100%", height: "auto" }}>
        <svg
          baseProfile="full"
          height="78.0"
          version="1.1"
          viewBox="0,0,66.0,78.0"
          width="66.0"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: "100%", height: "100%" }}
        >
          <rect fill="white" height="100%" width="100%" x="0" y="0" />
          <g fontSize="4">
            <text x="14.0" y="72.0">
              0
            </text>
            <text x="26.0" y="72.0">
              1
            </text>
            <text x="38.0" y="72.0">
              2
            </text>
            <text x="50.0" y="72.0">
              3
            </text>
            <text x="62.0" y="72.0">
              4
            </text>
            <text x="6.0" y="64.0">
              0
            </text>
            <text x="6.0" y="52.0">
              1
            </text>
            <text x="6.0" y="40.0">
              2
            </text>
            <text x="6.0" y="28.0">
              3
            </text>
            <text x="6.0" y="16.0">
              4
            </text>
            <text x="6.0" y="4.0">
              5
            </text>
          </g>
          <g
            style={{
              stroke: "gray",
              strokeWidth: ".1mm",
              strokeLinejoin: "round",
              strokeLinecap: "round",
              strokeDasharray: "1 1",
            }}
          >
            <line x1="15.0" x2="15.0" y1="44.76" y2="18.240000000000002" />
            <line x1="24.35" x2="24.35" y1="43.91" y2="17.39" />
            <line x1="32.85" x2="32.85" y1="43.91" y2="17.39" />
            <line x1="31.15" x2="31.15" y1="45.61" y2="19.090000000000003" />
            <line x1="22.65" x2="22.65" y1="45.61" y2="19.090000000000003" />
            <line x1="15.0" x2="15.0" y1="44.76" y2="18.240000000000002" />
          </g>
          <g
            style={{
              stroke: "black",
              strokeWidth: ".1mm",
              strokeLinejoin: "round",
              strokeLinecap: "round",
            }}
          >
            <line x1="15.0" x2="24.35" y1="63.0" y2="62.15" />
            <line x1="15.0" x2="24.35" y1="44.76" y2="43.91" />
            <line x1="15.0" x2="24.35" y1="18.240000000000002" y2="17.39" />
            <line x1="24.35" x2="32.85" y1="62.15" y2="62.15" />
            <line x1="24.35" x2="32.85" y1="43.91" y2="43.91" />
            <line x1="24.35" x2="32.85" y1="17.39" y2="17.39" />
            <line x1="32.85" x2="31.15" y1="62.15" y2="63.85" />
            <line x1="32.85" x2="31.15" y1="43.91" y2="45.61" />
            <line x1="32.85" x2="31.15" y1="17.39" y2="19.090000000000003" />
            <line x1="31.15" x2="22.65" y1="63.85" y2="63.85" />
            <line x1="31.15" x2="22.65" y1="45.61" y2="45.61" />
            <line
              x1="31.15"
              x2="22.65"
              y1="19.090000000000003"
              y2="19.090000000000003"
            />
            <line x1="22.65" x2="15.0" y1="63.85" y2="63.0" />
            <line x1="22.65" x2="15.0" y1="45.61" y2="44.76" />
            <line
              x1="22.65"
              x2="15.0"
              y1="19.090000000000003"
              y2="18.240000000000002"
            />
            <line x1="3.0" x2="63.0" y1="75.0" y2="75.0" />
            <line x1="3.0" x2="3.0" y1="75.0" y2="3.0" />
            <line x1="15.0" x2="15.0" y1="75.0" y2="73.0" />
            <line x1="27.0" x2="27.0" y1="75.0" y2="73.0" />
            <line x1="39.0" x2="39.0" y1="75.0" y2="73.0" />
            <line x1="51.0" x2="51.0" y1="75.0" y2="73.0" />
            <line x1="63.0" x2="63.0" y1="75.0" y2="73.0" />
            <line x1="3.0" x2="5.0" y1="63.0" y2="63.0" />
            <line x1="3.0" x2="5.0" y1="51.0" y2="51.0" />
            <line x1="3.0" x2="5.0" y1="39.0" y2="39.0" />
            <line x1="3.0" x2="5.0" y1="27.0" y2="27.0" />
            <line x1="3.0" x2="5.0" y1="15.0" y2="15.0" />
            <line x1="3.0" x2="5.0" y1="3.0" y2="3.0" />
          </g>
          <line x1="15.0" x2="24.35" y1="63.0" y2="62.15" />
          <line x1="15.0" x2="24.35" y1="44.76" y2="43.91" />
          <line x1="15.0" x2="24.35" y1="18.240000000000002" y2="17.39" />
          <line x1="24.35" x2="32.85" y1="62.15" y2="62.15" />
          <line x1="24.35" x2="32.85" y1="43.91" y2="43.91" />
          <line x1="24.35" x2="32.85" y1="17.39" y2="17.39" />
          <line x1="32.85" x2="31.15" y1="62.15" y2="63.85" />
          <line x1="32.85" x2="31.15" y1="43.91" y2="45.61" />
          <line x1="32.85" x2="31.15" y1="17.39" y2="19.090000000000003" />
          <line x1="31.15" x2="22.65" y1="63.85" y2="63.85" />
          <line x1="31.15" x2="22.65" y1="45.61" y2="45.61" />
          <line
            x1="31.15"
            x2="22.65"
            y1="19.090000000000003"
            y2="19.090000000000003"
          />
          <line x1="22.65" x2="15.0" y1="63.85" y2="63.0" />
          <line x1="22.65" x2="15.0" y1="45.61" y2="44.76" />
          <line
            x1="22.65"
            x2="15.0"
            y1="19.090000000000003"
            y2="18.240000000000002"
          />
          <line x1="15.0" x2="15.0" y1="44.76" y2="18.240000000000002" />
          <line x1="24.35" x2="24.35" y1="43.91" y2="17.39" />
          <line x1="32.85" x2="32.85" y1="43.91" y2="17.39" />
          <line x1="31.15" x2="31.15" y1="45.61" y2="19.090000000000003" />
          <line x1="22.65" x2="22.65" y1="45.61" y2="19.090000000000003" />
          <line x1="15.0" x2="15.0" y1="44.76" y2="18.240000000000002" />
          <line x1="3.0" x2="63.0" y1="75.0" y2="75.0" />
          <line x1="3.0" x2="3.0" y1="75.0" y2="3.0" />
          <line x1="15.0" x2="15.0" y1="75.0" y2="73.0" />
          <line x1="27.0" x2="27.0" y1="75.0" y2="73.0" />
          <line x1="39.0" x2="39.0" y1="75.0" y2="73.0" />
          <line x1="51.0" x2="51.0" y1="75.0" y2="73.0" />
          <line x1="63.0" x2="63.0" y1="75.0" y2="73.0" />
          <line x1="3.0" x2="5.0" y1="63.0" y2="63.0" />
          <line x1="3.0" x2="5.0" y1="51.0" y2="51.0" />
          <line x1="3.0" x2="5.0" y1="39.0" y2="39.0" />
          <line x1="3.0" x2="5.0" y1="27.0" y2="27.0" />
          <line x1="3.0" x2="5.0" y1="15.0" y2="15.0" />
          <line x1="3.0" x2="5.0" y1="3.0" y2="3.0" />

          <g>
            {coordinates?.map(({ yFeet, zFeet, result }, index) => (
              <circle
                key={index}
                cx={
                  ORIGIN_Y_IN_CHART_SCALE +
                  ((yFeet - WIDTH_MIN_IN_FEET) /
                    (WIDTH_MAX_IN_FEET - WIDTH_MIN_IN_FEET)) *
                    (WIDTH_MAX_IN_CHART_SCALE - WIDTH_MIN_IN_CHART_SCALE)
                }
                cy={
                  ORIGIN_Z_IN_CHART_SCALE -
                  ((zFeet - HEIGHT_MIN_IN_FEET) /
                    (HEIGHT_MAX_IN_FEET - HEIGHT_MIN_IN_FEET)) *
                    (HEIGHT_MAX_IN_CHART_SCALE - HEIGHT_MIN_IN_CHART_SCALE)
                }
                r={0.7}
                fill={getColorForCoordinate(result)}
                data-testid={`side-coordinate-circle-${index}`}
              />
            ))}
          </g>
        </svg>
      </div>

      {legendItems.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {legendItems.map(({ result }, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  backgroundColor: getColorForCoordinate(result),
                  borderRadius: "50%",
                }}
              />
              <span style={{ fontSize: "12px" }}>{result}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
